/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Fragment, useEffect, useRef } from 'react';

import { Button, Typography2 } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/expression-of-interest';

export type SurveyTerminalContent = {
  heading?: string;
  applyButtonText?: string;
  checklistItems?: {
    inboxDetails?: string;
    enrollmentTeam?: string;
    startApplication?: string;
  };
};

type Props = {
  onStartApplication?: () => void;
  // Moves keyboard focus onto the heading when the screen appears.
  focusHeadingOnMount?: boolean;
  content?: SurveyTerminalContent;
};

const styles = {
  heading: css`
    margin-bottom: 0;
  `,
  whatsNext: css`
    margin: var(--cds-spacing-200) 0 var(--cds-spacing-150);
  `,
  list: css`
    list-style: none;
    margin: 0 0 var(--cds-spacing-200);
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-150);
  `,
  listItem: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
  `,
  iconBadge: css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--cds-color-feedback-success);
  `,
  // Grayed checkmark for checklist steps the learner hasn't completed yet.
  iconBadgePending: css`
    background: var(--cds-color-emphasis-neutral-background-default);
  `,
};

const SurveyTerminalScreen = ({ onStartApplication, focusHeadingOnMount, content }: Props) => {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (focusHeadingOnMount) {
      headingRef.current?.focus();
    }
  }, [focusHeadingOnMount]);

  const checklistItems = [
    { text: content?.checklistItems?.inboxDetails, isPending: false },
    { text: content?.checklistItems?.enrollmentTeam, isPending: true },
    { text: content?.checklistItems?.startApplication, isPending: true },
  ].filter((item) => Boolean(item.text));

  return (
    <div data-testid="survey-terminal-screen">
      {content?.heading && (
        <Typography2 component="h3" variant="titleSmall" css={styles.heading} ref={headingRef} tabIndex={-1}>
          {content.heading}
        </Typography2>
      )}
      {checklistItems.length > 0 && (
        <Fragment>
          <Typography2 component="p" variant="bodyPrimary" css={styles.whatsNext}>
            {_t("What's next:")}
          </Typography2>
          <ul css={styles.list}>
            {checklistItems.map(({ text, isPending }) => (
              <li key={text} css={styles.listItem}>
                <span css={[styles.iconBadge, isPending && styles.iconBadgePending]} aria-hidden="true">
                  <CheckIcon size="small" color="invert" />
                </span>
                <Typography2 component="span" variant="bodyPrimary">
                  {text}
                </Typography2>
              </li>
            ))}
          </ul>
        </Fragment>
      )}
      {onStartApplication && (
        <Button variant="primary" onClick={onStartApplication}>
          {content?.applyButtonText || _t('Start application')}
        </Button>
      )}
    </div>
  );
};

export default SurveyTerminalScreen;
